/**
 * Cookie Consent Utilities
 *
 * Read/write helpers for the cookie_consent cookie.
 * The consent cookie is a Base64-encoded JSON payload stored as a
 * non-httpOnly first-party cookie so both client JS and the server
 * proxy can read it.
 *
 * The payload is Base64-encoded so that raw JSON never appears in a cookie
 * value, which trips Azure Front Door WAF SQLI false-positives.
 */

import { COOKIE_POLICY_VERSION } from "@/lib/constants/legal";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export interface CookieConsent {
  /** Always true -- not toggleable */
  necessary: true;
  /** User preference for UI-preference localStorage keys */
  functional: boolean;
  /** User preference for PostHog analytics */
  analytics: boolean;
  /** Reserved for future use */
  marketing: boolean;
  /** Policy version the user consented to */
  version: string;
  /** ISO-8601 timestamp */
  consentedAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COOKIE_NAME = "cookie_consent";
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

// PostHog localStorage keys share this prefix
const POSTHOG_STORAGE_PREFIX = "ph_";

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Parse the cookie_consent cookie from `document.cookie`.
 * Returns `null` when the cookie is missing or cannot be decoded.
 */
export function readCookieConsent(): CookieConsent | null {
  if (typeof document === "undefined") return null;

  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${COOKIE_NAME}=`));
    if (!match) return null;

    const value = match.split("=").slice(1).join("=");
    const json = atob(decodeURIComponent(value));
    const parsed = JSON.parse(json) as CookieConsent;

    if (typeof parsed.version !== "string" || parsed.necessary !== true) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Whether valid, up-to-date consent has already been recorded.
 */
export function hasValidConsent(): boolean {
  const consent = readCookieConsent();
  return consent !== null && consent.version === COOKIE_POLICY_VERSION;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Persist a consent choice as a Base64-encoded cookie.
 */
export function writeCookieConsent(
  choices: Pick<CookieConsent, "functional" | "analytics">,
): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    functional: choices.functional,
    analytics: choices.analytics,
    marketing: false,
    version: COOKIE_POLICY_VERSION,
    consentedAt: new Date().toISOString(),
  };

  const encoded = encodeURIComponent(btoa(JSON.stringify(consent)));
  const secure = globalThis.location?.protocol === "https:" ? "; Secure" : "";

  document.cookie = [
    `${COOKIE_NAME}=${encoded}`,
    `Path=/`,
    `Max-Age=${MAX_AGE_SECONDS}`,
    `SameSite=Lax`,
    secure,
  ].join("; ");

  return consent;
}

// ---------------------------------------------------------------------------
// Cleanup helpers
// ---------------------------------------------------------------------------

const POSTHOG_COOKIE_EXPIRE_DOMAINS = [
  undefined,
  "alterbiota.com",
  ".alterbiota.com",
] as const;

/**
 * Expire leftover PostHog cookies without touching localStorage.
 *
 * Consented PostHog persistence is localStorage-only. This removes leftover
 * `ph_*` cookies from the previous "localStorage+cookie" mode and from
 * parent-domain marketing-site cookies (`Domain=.alterbiota.com`) whose
 * JSON content triggers Azure Front Door WAF SQLI false positives.
 *
 * Host-only `Path=/` expiry does not delete a cookie set with
 * `Domain=.alterbiota.com`, so each discovered name is expired as host-only
 * and for `alterbiota.com` / `.alterbiota.com`. This only helps after the
 * page is allowed to load.
 */
export function clearPostHogCookies(): void {
  if (typeof document === "undefined") return;

  const names = new Set<string>();
  for (const row of document.cookie.split("; ")) {
    const name = row.split("=")[0];
    if (name?.startsWith(POSTHOG_STORAGE_PREFIX)) {
      names.add(name);
    }
  }

  const secure = globalThis.location?.protocol === "https:";

  for (const name of names) {
    for (const domain of POSTHOG_COOKIE_EXPIRE_DOMAINS) {
      const parts = [`${name}=`, "Path=/", "Max-Age=0", "SameSite=Lax"];
      if (secure) {
        parts.push("Secure");
      }
      if (domain) {
        parts.push(`Domain=${domain}`);
      }
      document.cookie = parts.join("; ");
    }
  }
}

/**
 * Remove all PostHog-related localStorage keys and cookies.
 * Called when the user revokes analytics consent.
 */
export function clearPostHogStorage(): void {
  if (globalThis.window === undefined) return;

  // localStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(POSTHOG_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  clearPostHogCookies();
}

/**
 * Remove all functional-preference localStorage keys.
 * Called when the user revokes functional consent.
 */
export function clearFunctionalStorage(): void {
  if (globalThis.window === undefined) return;

  const prefixes = [
    "sidebar-",
    "right-panel-",
    "dashboard-filters",
    "pinned-shape-size-descriptors",
    "da-completed-tours",
    "da-table-density",
    "da:shape-display-stat",
    "visionlab-table-collapsed",
    "visionlab-selected-parameter",
    "column-order-",
  ];

  const suffixes = [
    "-page-size",
    "-column-visibility",
    "-column-order",
    "-filters",
    "-filter-visibility",
    "-current-page",
    "-sorting",
  ];

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    const shouldRemove =
      prefixes.some((p) => key.includes(p)) ||
      suffixes.some((s) => key.endsWith(s));

    if (shouldRemove) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Clear storage that was revoked based on previous vs new consent.
 */
export function clearRevokedStorage(
  prev: CookieConsent | null,
  next: CookieConsent,
): void {
  if (prev?.analytics && !next.analytics) {
    clearPostHogStorage();
  }
  if (prev?.functional && !next.functional) {
    clearFunctionalStorage();
  }
}
